import { generateAPIUrl } from '@/utils/api-url';
import { skToPk } from '@/utils/encryption';
import { tokenCache } from '@/utils/token-cache';
import { router } from 'expo-router';
import { fetch as expoFetch } from 'expo/fetch';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

interface User {
  id: string;
  name: string;
  email: string;
  planKeyname: string;
  planInterval: string;
  debugMode: boolean;
}

export interface AuthState {
  token: string | null;
  encryptionKey: string | null;
  decryptionKey: string | null;
  authenticated: boolean;
  loading: boolean;
}

interface AuthProps {
  authState: AuthState;
  updateAuthState?: (props: { authState: AuthState; user?: User }) => void;
  logOut?: () => void;
  user?: User;
}

const AuthContext = createContext<AuthProps>({
  authState: {
    token: null,
    encryptionKey: null,
    decryptionKey: null,
    authenticated: false,
    loading: true
  }
});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>({
    token: null,
    encryptionKey: null,
    decryptionKey: null,
    authenticated: false,
    loading: true
  });

  const [user, setUser] = useState<User | undefined>();

  const getUser = async (token: string) => {
    try {
      const result = await expoFetch(generateAPIUrl('/api/auth'), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!result.ok) {
        if (result.status === 401) {
          throw new Error('Unauthorized');
        }
        throw new Error(`Error: ${result.status}`);
      }

      const data = await result.json();

      setUser({
        id: data.id as string,
        name: data.name as string,
        email: data.email as string,
        planKeyname: data.planKeyname as string,
        planInterval: data.planInterval as string,
        debugMode: data.debugMode as boolean
      });
      setAuthState({
        token,
        encryptionKey: skToPk(data.secretKey as string),
        decryptionKey: data.secretKey as string,
        authenticated: true,
        loading: false
      });
    } catch (e: unknown) {
      if (e instanceof Error && e.message === 'Unauthorized') {
        tokenCache.clearToken('auth-token');
        // router.dismissAll();
        router.replace('/auth/maybe-onboard');
        return;
      }

      throw e;
    }
  };

  useEffect(() => {
    const loadToken = async () => {
      let token: string | null = null;
      try {
        token = (await tokenCache.getToken('auth-token')) ?? null;
      } catch {
        // error reading value
      }

      if (token) {
        getUser(token);
      } else {
        setAuthState({
          token: null,
          encryptionKey: null,
          decryptionKey: null,
          authenticated: false,
          loading: false
        });

        // router.dismissAll();
        router.replace('/auth/maybe-onboard');
      }
    };

    loadToken();
  }, []);

  const updateAuthState = useCallback(
    ({ authState, user }: { authState: AuthState; user?: User }) => {
      setAuthState(authState);
      setUser(user);
    },
    []
  );

  const logOut = useCallback(async () => {
    tokenCache.clearToken('auth-token');
    setAuthState({
      token: null,
      encryptionKey: null,
      decryptionKey: null,
      authenticated: false,
      loading: false
    });
    router.navigate('/auth/login');
  }, []);

  const value = {
    authState,
    user,
    logOut,
    updateAuthState
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
